import React, { useMemo, useState } from 'react';
import * as Icons from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { leadsApi } from '../api/endpoints';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'all', label: 'All Leads' },
  { value: 'hot', label: 'Hot Leads' },
  { value: 'interested', label: 'Interested Leads' },
  { value: 'site_visit_completed', label: 'Site Visit Completed' },
  { value: 'avg_site_visits', label: 'Avg. Customer Site Visits' },
  { value: 'scheduled_site_visits', label: 'Scheduled Site Visits' },
  { value: 'todays_reminder', label: "Today's Reminder (pending)" },
];

const STATUS_OPTIONS = ['New', 'Contacted', 'Scheduled Site Visits', 'Avg Customer Site Visit', 'Site Visit Completed', 'Negotiation', 'Booked', 'Lost'];
const PRIORITY_OPTIONS = ['Hot', 'Interested', 'Average'];
const SOURCE_OPTIONS = ['Meta (Facebook)', 'Instagram', 'Google Ads', 'WhatsApp Campaign', 'Other Ads', 'Website', 'Referral', 'Walk-in', 'Magicbricks', '99acres'];

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function buildHtml(r) {
  const gen = new Date(r.generatedAt).toLocaleString('en-IN');
  const sum = r.summary;
  const leadBlocks = r.leads.map(l => `
    <div class="lead">
      <div class="lead-head">
        <span class="srno">${l.srNo}</span>
        <span class="lid">${esc(l.leadId)}</span>
        <span class="lname">${esc(l.name)}</span>
        ${l.overdue ? '<span class="overdue">OVERDUE / CARRIED FORWARD</span>' : ''}
      </div>
      <table class="details">
        <tr>
          <td><b>Mobile:</b> ${esc(l.phone)}${l.alternatePhone ? ` / Alt: ${esc(l.alternatePhone)}` : ''}</td>
          <td><b>Project:</b> ${esc(l.project) || '—'}</td>
          <td><b>Source:</b> ${esc(l.source) || '—'}</td>
        </tr>
        <tr>
          <td><b>Status:</b> ${esc(l.status)}</td>
          <td><b>Priority:</b> ${esc(l.priority)}</td>
          <td><b>Profession:</b> ${esc(l.profession) || '—'}</td>
        </tr>
        <tr>
          <td><b>Assigned Executive / Coordinator:</b> ${esc(l.owner) || 'Unassigned'}</td>
          <td><b>Created:</b> ${esc(l.createdAt)}</td>
          <td><b>Follow-up Date:</b> ${esc(l.followupDate) || '—'}</td>
        </tr>
        <tr>
          <td colspan="2"><b>Next Action:</b> ${esc(l.nextAction) || '—'}</td>
          <td><b>Next Action Date:</b> ${esc(l.followupDate) || '—'}</td>
        </tr>
        <tr><td colspan="3"><b>Current Remark:</b> ${esc(l.currentRemark) || '—'}</td></tr>
      </table>
      <div class="acts-title">Complete Activity History (${l.activities.length})</div>
      <table class="acts">
        <thead><tr><th>Sr.</th><th>Date</th><th>Time</th><th>Activity</th><th>Remark</th><th>User</th></tr></thead>
        <tbody>
          ${l.activities.map((a, i) => `<tr><td>${i + 1}</td><td>${esc(a.date)}</td><td>${esc(a.time)}</td><td>${esc(a.type)}</td><td class="remark">${esc(a.text)}</td><td>${esc(a.user)}</td></tr>`).join('')}
          ${l.activities.length === 0 ? '<tr><td colspan="6" style="text-align:center;color:#888">No activities recorded</td></tr>' : ''}
        </tbody>
      </table>
    </div>`).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(r.title)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a2436; margin: 24px; font-size: 12px; }
  .brand { display: flex; justify-content: space-between; align-items: baseline; border-bottom: 3px solid #C8A464; padding-bottom: 8px; }
  .brand h1 { margin: 0; font-size: 20px; color: #0F1F38; letter-spacing: 1px; }
  .brand .meta { font-size: 10px; color: #667; text-align: right; }
  h2.title { text-align: center; margin: 14px 0 4px; font-size: 15px; color: #0F1F38; letter-spacing: 0.5px; }
  .summary { width: 100%; border-collapse: collapse; margin: 10px 0 18px; }
  .summary th { background: #0F1F38; color: #fff; padding: 5px 4px; font-size: 10px; text-transform: uppercase; }
  .summary td { border: 1px solid #cdd5e0; text-align: center; padding: 6px 4px; font-size: 14px; font-weight: 700; }
  .lead { border: 1px solid #cdd5e0; border-radius: 6px; margin-bottom: 16px; page-break-inside: avoid; overflow: hidden; }
  .lead-head { background: #0F1F38; color: #fff; padding: 6px 10px; display: flex; align-items: center; gap: 8px; }
  .srno { background: #C8A464; color: #0F1F38; font-weight: 700; border-radius: 3px; padding: 1px 7px; }
  .lid { font-family: monospace; background: rgba(255,255,255,.15); padding: 1px 6px; border-radius: 3px; }
  .lname { font-weight: 700; font-size: 13px; }
  .overdue { margin-left: auto; background: #b91c1c; color: #fff; font-size: 9px; font-weight: 700; padding: 2px 6px; border-radius: 3px; }
  table.details { width: 100%; border-collapse: collapse; }
  table.details td { border: 1px solid #e3e8ef; padding: 4px 8px; width: 33%; }
  .acts-title { font-size: 10px; font-weight: 700; text-transform: uppercase; color: #556; padding: 6px 8px 2px; }
  table.acts { width: 100%; border-collapse: collapse; margin: 2px 0 0; }
  table.acts th { background: #eef2f7; border: 1px solid #dbe2ec; padding: 3px 6px; font-size: 10px; text-align: left; }
  table.acts td { border: 1px solid #e3e8ef; padding: 3px 6px; vertical-align: top; }
  table.acts td.remark { max-width: 320px; word-wrap: break-word; }
  .footer { text-align: center; color: #99a; font-size: 9px; margin-top: 20px; border-top: 1px solid #dde; padding-top: 6px; }
  @media print { body { margin: 8mm; } .lead { break-inside: avoid; } }
</style></head><body>
  <div class="brand">
    <h1>KIANA DEVELOPERS</h1>
    <div class="meta">Generated: ${esc(gen)}<br/>By: ${esc(r.generatedBy)}</div>
  </div>
  <h2 class="title">${esc(r.title)}</h2>
  <table class="summary">
    <thead><tr><th>Total Leads</th><th>Hot</th><th>Interested</th><th>Site Visits</th><th>Scheduled Visits</th><th>Missed Follow-ups</th><th>Booked</th></tr></thead>
    <tbody><tr><td>${sum.total}</td><td>${sum.hot}</td><td>${sum.interested}</td><td>${sum.siteVisitCompleted}</td><td>${sum.scheduledVisits}</td><td>${sum.missedFollowups}</td><td>${sum.booked}</td></tr></tbody>
  </table>
  ${leadBlocks}
  <div class="footer">KIANA DEVELOPERS CRM — Management Report · ${r.leads.length} lead(s) · Complete audit trail, no remarks deleted or overwritten</div>
  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };</script>
</body></html>`;
}

const PrintLeadsDialog = ({ open, onOpenChange, selectedIds = [], filteredIds = [], projects = [], owners = [] }) => {
  const [scope, setScope] = useState(selectedIds.length ? 'selected' : 'filtered');
  const [category, setCategory] = useState('all');
  const [filters, setFilters] = useState({ status: 'all', priority: 'all', property: 'all', source: 'all', owner: 'all', date_from: '', date_to: '' });
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const effScope = useMemo(() => (scope === 'selected' && !selectedIds.length ? 'filtered' : scope), [scope, selectedIds.length]);

  const runPrint = async () => {
    setBusy(true);
    try {
      const body = { category };
      if (effScope === 'selected') body.lead_ids = selectedIds;
      else if (effScope === 'filtered') body.lead_ids = filteredIds;
      else {
        for (const k of ['status', 'priority', 'property', 'source', 'owner']) {
          if (filters[k] !== 'all') body[k] = filters[k];
        }
        if (filters.date_from) body.date_from = filters.date_from;
        if (filters.date_to) body.date_to = filters.date_to;
      }
      const report = await leadsApi.printReport(body);
      if (!report.leads.length) { toast.error('No leads match the selected criteria'); return; }
      const win = window.open('', '_blank');
      if (!win) { toast.error('Popup blocked — please allow popups to print'); return; }
      win.document.write(buildHtml(report));
      win.document.close();
      toast.success(`Print report ready — ${report.leads.length} lead(s)`);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to build print report'); }
    finally { setBusy(false); }
  };

  const FilterSelect = ({ k, label, options }) => (
    <div>
      <Label className="text-[10px] uppercase font-bold">{label}</Label>
      <Select value={filters[k]} onValueChange={v => upd(k, v)}>
        <SelectTrigger className="h-8 text-xs" data-testid={`print-filter-${k}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="print-leads-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Icons.Printer className="w-5 h-5 text-brand-gold" /> Print Leads Report</DialogTitle>
          <DialogDescription className="text-xs">
            Prints complete lead details with the full activity history (audit trail) for management reporting.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-[11px] uppercase font-bold">Report Category (headline)</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9" data-testid="print-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[11px] uppercase font-bold">Which leads?</Label>
            <RadioGroup value={effScope} onValueChange={setScope} className="mt-1 space-y-1.5">
              <label className={`flex items-center gap-2 text-sm border rounded-md px-3 py-2 cursor-pointer ${selectedIds.length ? '' : 'opacity-50 pointer-events-none'}`}>
                <RadioGroupItem value="selected" data-testid="print-scope-selected" />
                Selected leads ({selectedIds.length})
              </label>
              <label className="flex items-center gap-2 text-sm border rounded-md px-3 py-2 cursor-pointer">
                <RadioGroupItem value="filtered" data-testid="print-scope-filtered" />
                All filtered leads on this page ({filteredIds.length})
              </label>
              <label className="flex items-center gap-2 text-sm border rounded-md px-3 py-2 cursor-pointer">
                <RadioGroupItem value="category" data-testid="print-scope-category" />
                By category + filters below (entire CRM)
              </label>
            </RadioGroup>
          </div>

          {effScope === 'category' && (
            <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="print-filters-panel">
              <FilterSelect k="status" label="Status" options={STATUS_OPTIONS} />
              <FilterSelect k="priority" label="Priority" options={PRIORITY_OPTIONS} />
              <FilterSelect k="property" label="Project" options={projects} />
              <FilterSelect k="source" label="Source" options={SOURCE_OPTIONS} />
              <FilterSelect k="owner" label="Sales Executive / Coordinator" options={owners} />
              <div className="grid grid-cols-2 gap-1.5 col-span-2 sm:col-span-1">
                <div>
                  <Label className="text-[10px] uppercase font-bold">From</Label>
                  <Input type="date" className="h-8 text-xs" value={filters.date_from} onChange={e => upd('date_from', e.target.value)} data-testid="print-date-from" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-bold">To</Label>
                  <Input type="date" className="h-8 text-xs" value={filters.date_to} onChange={e => upd('date_to', e.target.value)} data-testid="print-date-to" />
                </div>
              </div>
            </div>
          )}

          <Button className="w-full bg-brand-navy hover:bg-[#13294A]" onClick={runPrint} disabled={busy} data-testid="print-report-btn">
            {busy ? <Icons.Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Icons.Printer className="w-4 h-4 mr-2" />}
            Generate & Print Report
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrintLeadsDialog;
